import React, { useState, useEffect } from 'react';
import { XMarkIcon, ShareIcon, PlusIcon } from '@heroicons/react/24/outline';

interface IOSInstallPromptProps {
  onClose: () => void;
}

const IOSInstallPrompt: React.FC<IOSInstallPromptProps> = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Fade in animation
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for fade out animation
  };

  const handleInstallLater = () => {
    localStorage.setItem('ios-install-prompt-dismissed', Date.now().toString());
    handleClose();
  };

  const handleNeverShow = () => {
    localStorage.setItem('ios-install-prompt-never', 'true');
    handleClose();
  };

  return (
    <div
      className={`fixed inset-0 z-[9999] transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop */}
      <div
        className='absolute inset-0 bg-black/50 backdrop-blur-sm'
        onClick={handleClose}
      />

      {/* Popover */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl transform transition-transform duration-300 ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* Handle bar */}
        <div className='flex justify-center pt-3 pb-2'>
          <div className='w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full' />
        </div>

        {/* Content */}
        <div className='px-6 pb-safe'>
          {/* Header */}
          <div className='flex items-center justify-between mb-6'>
            <div className='flex items-center space-x-3'>
              {/* App Icon */}
              <div className='w-16 h-16 bg-gradient-to-br from-[#2A7B9B] to-[#57C785] rounded-2xl flex items-center justify-center shadow-lg'>
                <span className='text-white font-bold text-xl'>G</span>
              </div>
              <div>
                <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
                  Install GameChanger
                </h2>
                <p className='text-sm text-gray-600 dark:text-gray-400'>
                  Get the full app experience
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className='p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors'
            >
              <XMarkIcon className='w-5 h-5 text-gray-600 dark:text-gray-400' />
            </button>
          </div>

          {/* Instructions */}
          <div className='mb-8'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-4'>
              Add to your Home Screen for easy access
            </h3>

            {/* Step-by-step instructions */}
            <div className='space-y-4'>
              <div className='flex items-start space-x-4'>
                <div className='flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center'>
                  <span className='text-sm font-semibold text-blue-600 dark:text-blue-400'>
                    1
                  </span>
                </div>
                <div className='flex items-center space-x-2'>
                  <span className='text-gray-700 dark:text-gray-300'>
                    Tap the Share button
                  </span>
                  <div className='p-1.5 bg-blue-500 rounded-md'>
                    <ShareIcon className='w-4 h-4 text-white' />
                  </div>
                  <span className='text-gray-500 dark:text-gray-400 text-sm'>
                    in Safari
                  </span>
                </div>
              </div>

              <div className='flex items-start space-x-4'>
                <div className='flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center'>
                  <span className='text-sm font-semibold text-blue-600 dark:text-blue-400'>
                    2
                  </span>
                </div>
                <div className='flex items-center space-x-2'>
                  <span className='text-gray-700 dark:text-gray-300'>
                    Scroll and tap
                  </span>
                  <div className='flex items-center space-x-1 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700'>
                    <PlusIcon className='w-4 h-4 text-gray-600 dark:text-gray-400' />
                    <span className='text-sm text-gray-700 dark:text-gray-300'>
                      Add to Home Screen
                    </span>
                  </div>
                </div>
              </div>

              <div className='flex items-start space-x-4'>
                <div className='flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center'>
                  <span className='text-sm font-semibold text-blue-600 dark:text-blue-400'>
                    3
                  </span>
                </div>
                <div>
                  <span className='text-gray-700 dark:text-gray-300'>
                    Tap{' '}
                    <span className='font-medium text-blue-600 dark:text-blue-400'>
                      Add
                    </span>{' '}
                    to confirm
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className='mb-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl'>
            <h4 className='font-medium text-gray-900 dark:text-white mb-3'>
              Benefits of installing:
            </h4>
            <ul className='space-y-2 text-sm text-gray-600 dark:text-gray-400'>
              <li className='flex items-center space-x-2'>
                <div className='w-1.5 h-1.5 bg-green-500 rounded-full' />
                <span>Faster loading and offline access</span>
              </li>
              <li className='flex items-center space-x-2'>
                <div className='w-1.5 h-1.5 bg-green-500 rounded-full' />
                <span>Full-screen experience without browser UI</span>
              </li>
              <li className='flex items-center space-x-2'>
                <div className='w-1.5 h-1.5 bg-green-500 rounded-full' />
                <span>Quick access from your home screen</span>
              </li>
              <li className='flex items-center space-x-2'>
                <div className='w-1.5 h-1.5 bg-green-500 rounded-full' />
                <span>Native app-like performance</span>
              </li>
            </ul>
          </div>

          {/* Action buttons */}
          <div className='space-y-3 pb-6'>
            <button
              onClick={handleInstallLater}
              className='w-full py-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-2xl transition-colors shadow-lg'
            >
              Got it, I'll install it now
            </button>
            <button
              onClick={handleInstallLater}
              className='w-full py-3 text-gray-600 dark:text-gray-400 font-medium hover:text-gray-800 dark:hover:text-gray-200 transition-colors'
            >
              Maybe later
            </button>
            <button
              onClick={handleNeverShow}
              className='w-full py-2 text-sm text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors'
            >
              Don't show this again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IOSInstallPrompt;
